import React, { useEffect, useRef, useState } from "react";
import styles from "./CopyImageButton.module.css";
import {
  captureSvgAsPng,
  captureIframeAsPng,
} from "../CodeBlock/VisualizationExporter";

interface CopyImageButtonProps {
  /** Reference to the iframe rendering the visualization */
  iframeRef: React.RefObject<HTMLIFrameElement>;
  /** Language/type of the visualization */
  language: string;
  /** Optional direct SVG content for inline-rendered visuals (e.g. mermaid) */
  svgContent?: string;
  /** Optional tooltip */
  title?: string;
}

const SVG_LANGUAGES = new Set(["mermaid", "svg"]);

const CopyImageButton: React.FC<CopyImageButtonProps> = ({
  iframeRef,
  language,
  svgContent,
  title = "Copy image",
}) => {
  const [copied, setCopied] = useState(false);
  const [copying, setCopying] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  const isSvgBased = SVG_LANGUAGES.has(language);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, []);

  /**
   * Best-effort SVG extraction from iframe (for mermaid/svg renderers).
   */
  const getSvgStringFromIframe = (): string | null => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return null;

    const svgEl = doc.querySelector("svg");
    if (!svgEl) return null;

    return new XMLSerializer().serializeToString(svgEl);
  };

  const getSvgString = (): string | null => {
    if (svgContent) return svgContent;
    return getSvgStringFromIframe();
  };

  const handleCopyImage = async () => {
    if (copying) return;
    setCopying(true);

    try {
      let blob: Blob | null = null;

      // Prefer high-res SVG path when possible
      if (isSvgBased) {
        const svg = getSvgString();
        if (svg) {
          blob = await captureSvgAsPng(svg, 3);
        }
      }

      // Fallback to iframe screenshot
      if (!blob && iframeRef.current) {
        blob = await captureIframeAsPng(iframeRef.current);
      }

      if (!blob) return;

      // Clipboard write (image/png)
      // ClipboardItem is not always typed in TS depending on lib.dom versions
      const ClipboardItemCtor = (window as any).ClipboardItem as
        | (new (items: Record<string, Blob>) => ClipboardItem)
        | undefined;

      if (!ClipboardItemCtor || !navigator.clipboard?.write) {
        throw new Error(
          "Clipboard image API not available (needs secure context & modern browser).",
        );
      }

      const item = new ClipboardItemCtor({ "image/png": blob });
      await navigator.clipboard.write([item]);

      setCopied(true);
      timeoutRef.current = window.setTimeout(() => setCopied(false), 1200);
    } catch (e) {
      console.error("Copy image failed:", e);
    } finally {
      setCopying(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopyImage}
      className={`${styles.copyImageButton} ${copied ? styles.copied : ""}`}
      title={title}
      aria-label={title}
      disabled={copying}
    >
      {copying ? (
        <span className={styles.spinner} />
      ) : (
        // ✅ the image SVG we had earlier (Notion-style)
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <circle cx="9" cy="10" r="1.6" />
          <path d="M21 16l-4.5-4.5a2 2 0 0 0-2.8 0L6 19" />
        </svg>
      )}
    </button>
  );
};

export default CopyImageButton;
